import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useTicketRepository } from '../../data/hooks/useTicketRepository';
import {
  IncidentSubcategory,
  RequestSubcategory,
  Location,
} from '../../types/ticket';
import type { Ticket } from '../../types/ticket';
import type { CreateTicketDTO } from '../../types/dto';
import { classifyTicket, getTimeContext } from '../../utils/ticketIntelligence';
import { HelpRequestForm } from './components/HelpRequestForm';
import { SubmissionConfirmation } from './components/SubmissionConfirmation';
import './UserHelpCenter.css';

type ViewState = 'form' | 'success' | 'deflected';

interface NotificationState {
  type: 'success' | 'error' | 'info';
  message: string;
  visible: boolean;
}

const generateTicketNumber = (): string => {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `USR${timestamp}${random}`;
};

export function UserHelpCenter() {
  const { theme } = useTheme();
  const currentUser = useCurrentUser();
  const ticketRepo = useTicketRepository();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we were redirected from a successful deflection
  const initialView: ViewState = location.state?.deflected ? 'deflected' : 'form';
  const resolvedByArticle: string | undefined = location.state?.resolvedByArticle;

  const [viewState, setViewState] = useState<ViewState>(initialView);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedTicket, setSubmittedTicket] = useState<Ticket | null>(null);
  const [notification, setNotification] = useState<NotificationState>({
    type: 'info',
    message: '',
    visible: false,
  });

  const showNotification = useCallback((type: NotificationState['type'], message: string) => {
    setNotification({ type, message, visible: true });
    // Auto-hide after 5 seconds
    setTimeout(() => {
      setNotification((prev) => ({ ...prev, visible: false }));
    }, 5000);
  }, []);

  const dismissNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, visible: false }));
  }, []);

  const handleSubmit = useCallback(async (description: string) => {
    if (description.trim().length < 10) {
      showNotification('error', 'Please provide a bit more detail about your issue.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Auto-classify the ticket based on description
      const classification = classifyTicket({
        description: description.trim(),
        timeOfDay: getTimeContext(),
      });

      const ticketNumber = generateTicketNumber();

      // Determine the correct subcategory type based on ticket type
      const subcategory = classification.ticketType === 'Incident'
        ? (classification.subcategory as IncidentSubcategory)
        : (classification.subcategory as RequestSubcategory);

      // Create ticket via repository (user portal only creates Incident or Request types)
      const createDTO = {
        ticketNumber,
        shortDescription: description.trim(),
        ticketType: classification.ticketType,
        category: classification.category,
        subcategory,
        priority: classification.priority,
        location: currentUser.location ?? Location.US_HOUSTON,
        requesterId: currentUser.id,
        requesterName: currentUser.name,
        requesterEmail: currentUser.email,
        requesterDepartment: currentUser.department,
        createdBy: currentUser.id,
        createdByName: currentUser.name,
      } as CreateTicketDTO;

      const createdTicket = await ticketRepo.create(createDTO);

      setSubmittedTicket(createdTicket);
      setViewState('success');
    } catch (error) {
      console.error('Failed to create ticket:', error);
      showNotification(
        'error',
        'We couldn\'t submit your request. Please try again or contact IT support directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [ticketRepo, currentUser, showNotification]);

  const handleStartOver = useCallback(() => {
    setViewState('form');
    setSubmittedTicket(null);
    // Clear location state
    navigate('/help', { replace: true });
  }, [navigate]);

  const handleBrowseKB = useCallback(() => {
    navigate('/kb');
  }, [navigate]);

  const renderContent = () => {
    switch (viewState) {
      case 'form':
        return (
          <HelpRequestForm
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        );

      case 'success':
        if (!submittedTicket) {
          // Safety fallback - should not happen
          setViewState('form');
          return null;
        }
        return (
          <SubmissionConfirmation
            ticket={submittedTicket}
            onSubmitAnother={handleStartOver}
            onBrowseKB={handleBrowseKB}
          />
        );

      case 'deflected':
        return (
          <div className="help-deflected">
            <div className="help-deflected__icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="28" stroke="#107c10" strokeWidth="3" />
                <path
                  d="M20 32l8 8 16-20"
                  stroke="#107c10"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="help-deflected__title">Glad we could help!</h2>
            <p className="help-deflected__message">
              {resolvedByArticle
                ? `Great! "${resolvedByArticle}" resolved your issue.`
                : 'Looks like you found what you were looking for in our knowledge base.'}
            </p>
            <p className="help-deflected__submessage">
              If you still need assistance, we're always here to help.
            </p>
            <div className="help-deflected__actions">
              <button
                type="button"
                className="help-deflected__btn help-deflected__btn--primary"
                onClick={handleBrowseKB}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 3h5l2 2h7v10H2V3z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Browse Knowledge Base
              </button>
              <button
                type="button"
                className="help-deflected__btn"
                onClick={handleStartOver}
              >
                I Still Need Help
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="user-help-center" data-theme={theme}>
      {/* Notification Toast */}
      {notification.visible && (
        <div className={`help-notification help-notification--${notification.type}`}>
          <div className="help-notification__content">
            {notification.type === 'error' && (
              <svg
                className="help-notification__icon"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {notification.type === 'success' && (
              <svg
                className="help-notification__icon"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            <span className="help-notification__message">{notification.message}</span>
          </div>
          <button
            type="button"
            className="help-notification__dismiss"
            onClick={dismissNotification}
            aria-label="Dismiss notification"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4.28 3.22a.75.75 0 00-1.06 1.06L6.94 8l-3.72 3.72a.75.75 0 101.06 1.06L8 9.06l3.72 3.72a.75.75 0 101.06-1.06L9.06 8l3.72-3.72a.75.75 0 00-1.06-1.06L8 6.94 4.28 3.22z" />
            </svg>
          </button>
        </div>
      )}

      <div className="user-help-center__container">
        {/* Header */}
        <header className="user-help-center__header">
          <div className="user-help-center__logo">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <rect x="2" y="6" width="28" height="20" rx="2" stroke="currentColor" strokeWidth="2" />
              <path d="M2 10h28" stroke="currentColor" strokeWidth="2" />
              <circle cx="6" cy="8" r="1" fill="currentColor" />
              <circle cx="10" cy="8" r="1" fill="currentColor" />
            </svg>
            <span>IT Help Center</span>
          </div>
          <div className="user-help-center__user">
            <div className="user-help-center__user-avatar">
              {currentUser.name.charAt(0).toUpperCase()}
            </div>
            <span className="user-help-center__user-name">{currentUser.name}</span>
          </div>
        </header>

        {/* Content */}
        <main className="user-help-center__content">
          {renderContent()}
        </main>

        {/* Footer */}
        <footer className="user-help-center__footer">
          <p>
            Need immediate help? Contact IT Support at <strong>ext. 5555</strong>
          </p>
        </footer>
      </div>
    </div>
  );
}
